// src/components/TicketTable.jsx

import React, { useState } from "react";
import {
  Box,
  Chip,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
} from "@mui/material";
import {
  STATUS_STYLES,
  IMPORTANCE_COLORS,
  ASSIGNEE_STYLES,
  CHIP_FONT_SIZE,
} from "../theme/constants";

const getStatusStyles = (status) =>
  STATUS_STYLES[status?.toLowerCase()] || STATUS_STYLES.default;

const getImportanceStyles = (importance) =>
  IMPORTANCE_COLORS[importance?.toLowerCase()] || IMPORTANCE_COLORS.default;

const TicketTable = ({
  title,
  tickets = [],
  onRowClick,
  showAssignee = true,
  showImportance = true,
  renderAssignee,
}) => {
  const theme = useTheme();
  const headerBg = theme.palette.mode === "dark" ? "#187089" : "#001726";

  const headerCellStyle = {
    backgroundColor: headerBg,
    color: "#fff",
    fontWeight: 600,
    fontSize: {
      xs: "12px",
      sm: "13px",
      md: "14px",
    },
    cursor: "pointer",
    userSelect: "none",
  };

  const bodyCellStyle = {
    fontSize: {
      xs: "12px",
      sm: "13px",
      md: "14px",
    },
  };

  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedTickets = React.useMemo(() => {
    if (!sortConfig.key) return tickets;

    return [...tickets].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [tickets, sortConfig]);

  const renderSortIndicator = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === "asc" ? (
        <span style={{ marginLeft: 4 }}>▲</span>
      ) : (
        <span style={{ marginLeft: 4 }}>▼</span>
      );
    }
    return (
      <span style={{ marginLeft: 4, color: "#aaa", fontSize: "0.85em" }}>▲▼</span>
    );
  };

  return (
    <Box
      m="20px"
      display="flex"
      flexDirection="column"
      height="calc(100vh - 150px)"
      pb="40px"
    >
      {title && (
        <Typography variant="h4" gutterBottom>
          {title}
        </Typography>
      )}

      <TableContainer
        component={Paper}
        sx={{
          flex: 1,
          overflowY: "auto",
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellStyle} onClick={() => handleSort("id")}>
                ID {renderSortIndicator("id")}
              </TableCell>
              <TableCell
                sx={headerCellStyle}
                onClick={() => handleSort("email_sender")}
              >
                Sender {renderSortIndicator("email_sender")}
              </TableCell>
              <TableCell
                sx={headerCellStyle}
                onClick={() => handleSort("email_subject")}
              >
                Subject {renderSortIndicator("email_subject")}
              </TableCell>
              {showAssignee && (
                <TableCell
                  sx={headerCellStyle}
                  onClick={() => handleSort("assigned_to")}
                >
                  Assigned To {renderSortIndicator("assigned_to")}
                </TableCell>
              )}
              <TableCell
                sx={headerCellStyle}
                onClick={() => handleSort("status")}
              >
                Status {renderSortIndicator("status")}
              </TableCell>
              <TableCell
                sx={headerCellStyle}
                onClick={() => handleSort("created_at")}
              >
                Created Date {renderSortIndicator("created_at")}
              </TableCell>
              {showImportance && (
                <TableCell
                  sx={headerCellStyle}
                  onClick={() => handleSort("importance")}
                >
                  Importance {renderSortIndicator("importance")}
                </TableCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedTickets.map((ticket) => {
              const status = getStatusStyles(ticket.status);
              const importance = getImportanceStyles(ticket.importance);

              return (
                <TableRow
                  key={ticket.id}
                  hover
                  onClick={() => onRowClick && onRowClick(ticket)}
                  style={{ cursor: onRowClick ? "pointer" : "default" }}
                >
                  <TableCell sx={bodyCellStyle}>{ticket.id}</TableCell>
                  <TableCell sx={bodyCellStyle}>
                    {ticket.sender_name || ticket.email_sender}
                  </TableCell>
                  <TableCell sx={bodyCellStyle}>
                    {ticket.email_subject}
                  </TableCell>

                  {showAssignee && (
                    <TableCell sx={bodyCellStyle}>
                      {renderAssignee ? (
                        renderAssignee(ticket)
                      ) : (
                        ticket.assigned_to && (
                          <Chip
                            label={ticket.assigned_to}
                            sx={{
                              backgroundColor: ASSIGNEE_STYLES.background,
                              color: ASSIGNEE_STYLES.color,
                              fontWeight: 500,
                              "& .MuiChip-label": {
                                fontSize: CHIP_FONT_SIZE,
                              },
                            }}
                          />
                        )
                      )}
                    </TableCell>
                  )}

                  <TableCell sx={{ ...bodyCellStyle, verticalAlign: "middle" }}>
                    <Box display="flex" alignItems="center">
                      <Chip
                        label={status.label}
                        sx={{
                          backgroundColor: status.background,
                          color: status.color,
                          "& .MuiChip-label": {
                            fontSize: CHIP_FONT_SIZE,
                          },
                        }}
                      />
                    </Box>
                  </TableCell>

                  <TableCell sx={bodyCellStyle}>
                    {new Date(ticket.created_at).toLocaleString()}
                  </TableCell>

                  {showImportance && (
                    <TableCell
                      sx={{ ...bodyCellStyle, verticalAlign: "middle" }}
                    >
                      <Box display="flex" alignItems="center">
                        <Chip
                          label={ticket.importance}
                          sx={{
                            backgroundColor: importance.background,
                            color: importance.color,
                            "& .MuiChip-label": {
                              fontSize: CHIP_FONT_SIZE,
                            },
                          }}
                        />
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default TicketTable;